import { type ReactElement } from 'react';
import { querySuiteRunStatusSummary } from '@/features/executions/data';
import {
  buildSuiteCounts,
  type SuiteStatusKey,
} from '@/features/quality/components/qualityStatusDataHelpers';
import { EntityTrendsPageShell } from '@/features/shared/components/entityTrends/EntityTrendsPageShell';
import { useEntityTrendsPage } from '@/features/shared/hooks/useEntityTrendsPage';
import { SuiteRunsTable } from '@/features/suites/components/SuiteRunsTable';
import { SuitesTable } from '@/features/suites/components/SuitesTable';
import { querySuites, querySuiteSummary } from '@/features/suites/data/suites';
import { SuitesPageContent } from '@/features/suites/pages/SuitesPageContent';
import { suiteRunStatusOptions } from '@/shared/constants/statusOptions';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { getLoadingMessage } from '@/shared/copy/loading';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { Stack } from '@/ui';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  SuiteRunStatusSummaryDto,
  SuiteListItemDto,
  SuiteSummaryDto,
} from '@flowqa/shared';

const SuitesPageInner = (): ReactElement => {
  const viewModel = useEntityTrendsPage<
    SuiteListItemDto,
    SuiteSummaryDto | SuiteRunStatusSummaryDto
  >({
    loadingMessage: getLoadingMessage('suites'),
    metricsScope: 'suite',
    loadExecutionMetrics: false,
    gridPath: gridPaths.suites,
    executionGridPath: gridPaths.suiteRuns,
    entitySearchField: 'name',
    executionSearchField: 'suiteName',
    statusOptions: suiteRunStatusOptions.map((option) => ({
      value: option.value as SuiteStatusKey,
      label: option.label,
    })),
    summaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.suiteSummary(projectId, organisationId, filtersKey),
    executionSummaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.suiteRunSummary(projectId, organisationId, filtersKey),
    summaryPrefixKey: queryKeys.suiteSummaryPrefix,
    executionSummaryPrefixKey: queryKeys.suiteRunSummaryPrefix,
    queryLatestSummary: querySuiteSummary,
    queryExecutionSummary: querySuiteRunStatusSummary,
    buildCounts: buildSuiteCounts,
    selectThresholds: (thresholds) => thresholds.suites,
    queryEntityTable: async (
      input: GridQueryInput,
      {
        selectedProjectId,
        selectedOrganisationId,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled: _deltasEnabled,
      },
    ): Promise<QueryResult<SuiteListItemDto>> =>
      querySuites(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        metricsStartDay: rangeStartIso ?? undefined,
        metricsEndDay: rangeEndIso ?? undefined,
        previousMetricsStartDay: previousRangeStartIso ?? undefined,
        previousMetricsEndDay: previousRangeEndIso ?? undefined,
        include: {
          latestRun: true,
          executionStatusCounts: true,
          executionSummaries: true,
          latestTestResults: { counts: true, summaries: true },
          metrics: true,
        },
      }),
  });
  const suitesEmptyState = getEmptyStateMessage('entity.noneFound', { entity: 'suite' });
  const suiteRunsEmptyState = getEmptyStateMessage('entity.noneFound', { entity: 'suiteRun' });
  return (
    <Stack gap="md">
      <EntityTrendsPageShell
        viewModel={viewModel}
        title="Suites"
        addButtonLabel="Add Suite"
        searchAriaLabel="Search suites"
        chartTabsAriaLabel="Suites chart tabs"
        pageTabsAriaLabel="Suites page tabs"
        entityTabLabel="Suites"
        chartEmptyStateTitle={suiteRunsEmptyState}
        entityTableNoRowsText={suitesEmptyState}
        executionTableNoRowsText={suiteRunsEmptyState}
        contentOverride={<SuitesPageContent viewModel={viewModel} />}
        renderEntityTable={({ showDeltas, noRowsText }) => (
          <SuitesTable
            persistKey="suites-table"
            queryKey={viewModel.queryKey}
            queryFn={viewModel.queryFn}
            tagOptionsOverride={viewModel.tagOptions}
            initialPageSize={5}
            showDeltas={showDeltas}
            fixedFilters={viewModel.entityTableFilters}
            search={viewModel.debouncedSearchValue}
            noRowsText={noRowsText}
            enableMetricsBatch={false}
            metricsBatchDelayMs={1500}
            blockOnMetricsBatch={false}
          />
        )}
        renderExecutionsTable={({ noRowsText }) => (
          <SuiteRunsTable
            persistKey="suite-runs-table"
            fixedFilters={[...viewModel.executionFilters, ...viewModel.executionStatusFilters]}
            tagOptionsOverride={viewModel.tagOptions}
            initialPageSize={5}
            search={viewModel.debouncedSearchValue}
            noRowsText={noRowsText}
          />
        )}
      />
    </Stack>
  );
};

export const SuitesPage = (): ReactElement => {
  return <SuitesPageInner />;
};
